import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { delIcon, editIcon, eyeClosed, publishIcon } from "assets/icons";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import EmptyState from "../../components/EmptyState";
import { ConfirmModal } from "../../components/Modals";
import Sidebar from "../../components/Sidebar";
import StatusBadge from "../../components/StatusBadge";
import Table, { TableText } from "../../components/Table";
import { useNotify } from "../../context/NotificationContext";
import { useListPage } from "../../hooks/useListPage";
import {
  createNewsArticle,
  deleteNewsArticle,
  fetchManagedNews,
  publishNewsArticle,
  unpublishNewsArticle,
} from "../../services/newsService/newsService";
import type {
  NewsArticleStatus,
  NewsArticleSummary,
  NewsFilterOption,
  PaginatedNews,
} from "../../services/newsService/newsService.types";
import {
  ButtonWrapper,
  ButtonsContainer,
  ContentWrapper,
  CoursesPageContainer,
  HeaderContainer,
  Title,
} from "../CoursesPage/CoursesPage.styles";

const statusLabel: Record<NewsArticleStatus, string> = {
  draft: "Черновик",
  published: "Опубликовано",
};

const statusVariant: Record<NewsArticleStatus, "red" | "brand"> = {
  draft: "red",
  published: "brand",
};

const defaultStatusFilterOptions: NewsFilterOption[] = (
  Object.entries(statusLabel) as Array<[NewsArticleStatus, string]>
).map(([value, label]) => ({ label, value }));

const formatUpdatedAt = (value: string) =>
  new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));

const formatDateParam = (value: Date | null) => {
  if (!value) return undefined;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const rowAmountOptions = [
  { label: "7 статей", value: 7 },
  { label: "15 статей", value: 15 },
  { label: "20 статей", value: 20 },
];

type PendingAction =
  | { type: "publish"; article: NewsArticleSummary }
  | { type: "unpublish"; article: NewsArticleSummary }
  | { type: "delete"; article: NewsArticleSummary }
  | null;

const NewsManagePage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useNotify();

  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [titleFilterOptions, setTitleFilterOptions] = useState<NewsFilterOption[]>([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState<NewsFilterOption[]>(
    defaultStatusFilterOptions,
  );
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const filterKey = JSON.stringify({
    filters,
    updatedFrom: formatDateParam(filterStartDate),
    updatedTo: formatDateParam(filterEndDate),
  });

  const {
    items: articles,
    loading,
    error,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload,
  } = useListPage<NewsArticleSummary, PaginatedNews>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchManagedNews({
        titles: filters.title?.length ? filters.title : undefined,
        statuses: filters.status?.length
          ? (filters.status as NewsArticleStatus[])
          : undefined,
        updatedFrom: formatDateParam(filterStartDate),
        updatedTo: formatDateParam(filterEndDate),
        ordering: orderBy || undefined,
        page,
        pageSize: size,
      }),
    select: (response) => ({ items: response.results, total: response.count }),
    onLoaded: (response) => {
      setTitleFilterOptions(response.filter_options?.titles ?? []);
      setStatusFilterOptions(
        response.filter_options?.statuses?.length
          ? response.filter_options.statuses
          : defaultStatusFilterOptions,
      );
    },
    errorMessage: "Не удалось загрузить управление новостями",
    initialPageSize: 7,
    reloadKey: filterKey,
  });

  const handleFilterSave = (key: string, selected: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: selected }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleCalendarChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setFilterStartDate(start);
    setFilterEndDate(end);
    setCurrentPage(1);
    if (start && end) {
      setActiveFilter(null);
    }
  };

  const createArticle = async () => {
    if (creating) return;

    setCreating(true);
    try {
      const article = await createNewsArticle({
        content_json: { time: null, blocks: [], version: "2.30.8" },
      });
      navigate("/news/" + article.id + "/edit", { state: { newDraft: true } });
    } catch {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось создать черновик статьи",
        life: 3000,
      });
    } finally {
      setCreating(false);
    }
  };

  const handlePublish = async (article: NewsArticleSummary) => {
    try {
      await publishNewsArticle(article.id);
      await reload();
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: "Статья опубликована",
        life: 2500,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось опубликовать статью",
        life: 3000,
      });
    }
  };

  const handleUnpublish = async (article: NewsArticleSummary) => {
    try {
      await unpublishNewsArticle(article.id);
      await reload();
      toast.show({
        severity: "success",
        summary: "Готово",
        detail: "Статья снята с публикации и возвращена в черновики",
        life: 2500,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось снять статью с публикации",
        life: 3000,
      });
    }
  };

  const handleDelete = async (article: NewsArticleSummary) => {
    try {
      await deleteNewsArticle(article.id);

      if (articles.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        await reload();
      }

      toast.show({
        severity: "success",
        summary: "Удалено",
        detail: "Статья удалена",
        life: 2500,
      });
    } catch {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Не удалось удалить статью",
        life: 3000,
      });
    }
  };

  const confirmPendingAction = async () => {
    if (!pendingAction) return;

    const action = pendingAction;
    setPendingAction(null);

    if (action.type === "publish") {
      await handlePublish(action.article);
      return;
    }

    if (action.type === "unpublish") {
      await handleUnpublish(action.article);
      return;
    }

    await handleDelete(action.article);
  };

  const headers = [
    { label: "№", key: "number", fieldName: "number", width: "6%" },
    {
      label: "Название",
      key: "title",
      width: "50%",
      fieldName: "title",
      isFilter: true,
      filterType: "list" as const,
      listOptions: titleFilterOptions,
      listFilter: {
        title: "Название",
        searchable: true,
        searchPlaceholder: "Найти статью",
        emptyMessage: "Статьи не найдены",
      },
    },
    {
      label: "Статус",
      key: "status",
      width: "14%",
      fieldName: "status",
      isFilter: true,
      filterType: "list" as const,
      listOptions: statusFilterOptions,
      listFilter: {
        title: "Статус",
        searchable: false,
      },
    },
    {
      label: "Изменено",
      key: "updated",
      width: "18%",
      fieldName: "updated_at",
      isFilter: true,
      filterType: "calendar" as const,
    },
    { label: "", key: "actions", width: "12%", sortable: false },
  ];

  const rows = articles.map((article) => ({
    id: article.id,
    number: (
      <TableText numeric tone={article.number ? "default" : "muted"}>
        {article.number ?? "—"}
      </TableText>
    ),
    title: <TableText>{article.title || "Без названия"}</TableText>,
    status: (
      <StatusBadge
        text={statusLabel[article.status]}
        variant={statusVariant[article.status]}
      />
    ),
    updated: <TableText>{formatUpdatedAt(article.updated_at)}</TableText>,
    actions: (
      <ButtonsContainer>
        <IconButton
          icon={editIcon}
          altText="Редактировать"
          onClick={() => navigate("/news/" + article.id + "/edit")}
        />

        {article.status === "published" ? (
          <IconButton
            icon={eyeClosed}
            altText="Снять с публикации"
            onClick={() => setPendingAction({ type: "unpublish", article })}
          />
        ) : (
          <IconButton
            icon={publishIcon}
            altText="Опубликовать"
            onClick={() => setPendingAction({ type: "publish", article })}
          />
        )}

        <IconButton
          icon={delIcon}
          tone="danger"
          altText="Удалить"
          onClick={() => setPendingAction({ type: "delete", article })}
        />
      </ButtonsContainer>
    ),
  }));

  const emptyState = {
    title: "Статей пока нет",
    description: "Создайте первую статью.",
  };

  return (
    <CoursesPageContainer>
      <Sidebar />
      <ContentWrapper>
        <Breadcrumbs
          items={[
            { label: "Новости", to: "/news" },
            { label: "Управление новостями" },
          ]}
        />

        <HeaderContainer>
          <Title>Управление новостями</Title>
          <ButtonWrapper>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => void createArticle()}
              disabled={creating}
            >
              {creating ? "Создаём..." : "Новая статья"}
            </Button>
          </ButtonWrapper>
        </HeaderContainer>

        {error ? (
          <EmptyState
            title="Не удалось загрузить статьи"
            description="Обновите страницу или попробуйте ещё раз."
          />
        ) : (
          <Table
            headers={headers}
            rows={rows}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowAmountOptions={rowAmountOptions}
            onSort={handleSort}
            onRowClick={(row) => navigate("/news/" + row.id + "/edit")}
            emptyState={emptyState}
            filteredEmptyState={{
              title: "Ничего не найдено",
              description: "Измените или сбросьте фильтры.",
            }}
            loading={loading}
            activeFilter={activeFilter}
            handleActiveFilterChange={setActiveFilter}
            onCalendarChange={handleCalendarChange}
            filterStartDate={filterStartDate || undefined}
            filterEndDate={filterEndDate || undefined}
            appliedFilters={filters}
            onFilterSave={handleFilterSave}
            onFilterReset={handleFilterReset}
          />
        )}

        <ConfirmModal
          isOpen={Boolean(pendingAction)}
          title={
            pendingAction?.type === "publish"
              ? "Опубликовать статью?"
              : pendingAction?.type === "unpublish"
              ? "Снять статью с публикации?"
              : "Удалить статью?"
          }
          message={
            pendingAction?.type === "publish"
              ? "Статья «" +
                pendingAction.article.title +
                "» станет доступна пользователям в разделе «Новости»."
              : pendingAction?.type === "unpublish"
              ? "Статья «" +
                pendingAction.article.title +
                "» исчезнет из пользовательской ленты и вернётся в черновики. Номер публикации сохранится."
              : pendingAction?.type === "delete"
              ? "Статья «" +
                pendingAction.article.title +
                "» и связанные с ней материалы будут удалены без возможности восстановления."
              : ""
          }
          confirmButtonText={
            pendingAction?.type === "publish"
              ? "Опубликовать"
              : pendingAction?.type === "unpublish"
              ? "Снять с публикации"
              : "Удалить"
          }
          confirmVariant={pendingAction?.type === "delete" ? "danger" : "primary"}
          onConfirm={() => void confirmPendingAction()}
          onCancel={() => setPendingAction(null)}
        />

      </ContentWrapper>
    </CoursesPageContainer>
  );
};

export default NewsManagePage;